/**
 * SIDEBET_BETTING (craps) — sidebet window. Mirrors cee-lo's
 * PlayerBettingState shape: wallet open for everyone, lock-in button
 * visible, single banner copy.
 *
 * Per the locked design, this state is entered:
 *   1. Always after FADING (initial post-fade window — losers of the
 *      fade race need a chance to bet on the shooter wins/loses)
 *   2. After POINT_ESTABLISHED / POINT_RESULT when at least one
 *      player pre-armed the sidebet button (boundary check in
 *      CrapsStrategy.getNextState)
 *
 * Lock-in button set ONCE in onEnter — calling it in onUpdate would
 * re-run the intro tween every game:update tick.
 */

import type { GameUpdate } from '@sdl/shared';
import { CrapsState } from '@sdl/shared';
import { State } from '../State.js';

export class SidebetBettingState extends State {
  async onEnter(update: GameUpdate): Promise<void> {
    const game = this.game;

    game.playerProfilesOverlay?.show();
    game.playerProfilesOverlay?.setMode('betting');
    game.countdownOverlay?.stop();

    await game.enterBettingMode({
      open: true,
      animated: update.animated,
      previousState: update.previousState,
    });

    game.bettingHUD3D?.setLockInVisible(true);
    // Lock in the sidebet selector options for THIS window from the
    // authoritative update (point set → point pairs; pre-come-out → SW/SL).
    game.primeSidebetOptions(update);
    // Title-only banner — the subtitle sat in the strip the sidebet
    // zones grow into and overlapped the bills once 3+ were placed
    // (same fix as cee-lo's PlayerBettingState).
    game.instructionBanner?.setSubtitleVisible(false);
    game.instructionBanner?.show(
      { title: 'Sidebets', text: 'Place a sidebet' },
      update.animated,
    );

    this._updateProfiles(update);
    await this._checkPlayers(update);
  }

  async onUpdate(update: GameUpdate): Promise<void> {
    // SIDEBET_BETTING in craps doesn't have data-driven banner changes
    // (unlike cee-lo's bankerHasRolled flip). Just refresh players.
    this._updateProfiles(update);
    await this._checkPlayers(update);
  }

  async onExit(update: GameUpdate): Promise<void> {
    // Clear the BETTING activity pill — leaving the sidebet window.
    this._clearBettingActivity();

    // Preload the shooter's ready dice + name label BEFORE the rolling
    // state's onEnter runs exitBettingMode. Staged in scene at the
    // pickup pose, so they're visible the moment the camera arrives at
    // the shooting anchor — no pop-in mid-lerp.
    //
    // Idempotent with the rolling state's own _showOpponentReadyDice
    // call (kept for late-join robustness when state:full enters the
    // rolling state directly without an onExit here).
    if (update.gameState === CrapsState.COME_OUT
        || update.gameState === CrapsState.POINT_ROLL) {
      const rollerId = update.currentPlayerId ?? update.bankerId;
      this._showOpponentReadyDice(rollerId, 2);

      // A fade that completes a sidebet zone can ride the SAME update
      // that ends the window. Hold the transition until the fader's
      // bills finish dropping and the locked zone consolidates — same
      // race as heads-up FADING → COME_OUT (see CrapsFadingState).
      // Resolves immediately when nothing is pending; settle-guaranteed.
      if (update.animated) {
        await this.game.bettingController?.waitForLockedZoneTeardown();
      }
    }
  }
}
